import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase';

// sessionStorage key: access tokens are short-lived (~1h) so this matches
// their typical useful lifetime. We drop the cache on 401 responses.
const STORAGE_KEY = 'synthweb.drive.accessToken';

function readCached(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeCached(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* storage not available — proceed without caching */
  }
}

export function clearDriveAccessToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Prompts the user for Drive scope via Firebase Google sign-in popup and
 * returns the resulting OAuth access token.
 *
 * The token is cached in `sessionStorage`. Call `clearDriveAccessToken()`
 * and re-invoke on 401 from a Drive-backed worker endpoint.
 */
export async function getDriveAccessToken(force = false): Promise<string> {
  if (!force) {
    const cached = readCached();
    if (cached) return cached;
  }

  const provider = new GoogleAuthProvider();
  provider.addScope('https://www.googleapis.com/auth/drive.file');
  // Prompt even when already signed in so the user explicitly consents to
  // the Drive scope the first time.
  provider.setCustomParameters({ prompt: 'consent' });

  const result = await signInWithPopup(auth, provider);
  const credential = GoogleAuthProvider.credentialFromResult(result);
  const token = credential?.accessToken;
  if (!token) {
    throw new Error('Google sign-in did not return a Drive access token.');
  }
  writeCached(token);
  return token;
}

/**
 * Wrapper for a Drive-dependent worker call. On 401 it clears the cache,
 * re-prompts for a new token once, and retries.
 */
export async function withDriveToken<T>(
  fn: (token: string) => Promise<T>,
): Promise<T> {
  let token = await getDriveAccessToken(false);
  try {
    return await fn(token);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/\b401\b|unauthorized|invalid_token/i.test(msg)) {
      clearDriveAccessToken();
      token = await getDriveAccessToken(true);
      return await fn(token);
    }
    throw err;
  }
}
